/**
 * Symbol Management Panel
 *
 * UI for managing the dynamic symbol registry.
 * Add, edit, and remove trading symbols without code changes.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useSymbolStore, SymbolDefinition } from '../store/symbolStore';
import { DataTable } from './DataTable';
import './SymbolManagementPanel.css';

interface SymbolManagementPanelProps {
  className?: string;
}

const EMPTY_SYMBOL: SymbolDefinition = {
  base: '',
  fullSymbol: '',
  label: '',
  tickSize: 0.25,
  tickValue: 5.0,
  pointValue: 20.0,
  exchange: 'CME',
  enabled: true,
};

const EXCHANGES = ['CME', 'COMEX', 'NYMEX', 'CBOT', 'NYBOT'];

const SymbolManagementPanel: React.FC<SymbolManagementPanelProps> = ({ className }) => {
  const { symbols, isLoading, error, fetchSymbols, addSymbol, removeSymbol } = useSymbolStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editingSymbol, setEditingSymbol] = useState<SymbolDefinition | null>(null);
  const [formData, setFormData] = useState<SymbolDefinition>(EMPTY_SYMBOL);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Refresh symbols on mount
  useEffect(() => {
    fetchSymbols();
  }, [fetchSymbols]);

  const handleAdd = () => {
    setEditingSymbol(null);
    setFormData(EMPTY_SYMBOL);
    setIsEditing(true);
    setSaveError(null);
  };

  const handleEdit = (symbol: SymbolDefinition) => {
    setEditingSymbol(symbol);
    setFormData({ ...symbol });
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingSymbol(null);
    setFormData(EMPTY_SYMBOL);
    setSaveError(null);
  };

  const handleSave = async () => {
    // Validate
    if (!formData.base.trim()) {
      setSaveError('Base symbol is required');
      return;
    }
    if (!formData.fullSymbol.trim()) {
      setSaveError('Full symbol is required');
      return;
    }
    if (formData.tickSize <= 0) {
      setSaveError('Tick size must be positive');
      return;
    }
    if (formData.pointValue <= 0) {
      setSaveError('Point value must be positive');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const success = await addSymbol({
      ...formData,
      base: formData.base.toUpperCase().trim(),
      fullSymbol: formData.fullSymbol.toUpperCase().trim(),
      label: formData.label.trim() || `${formData.base.toUpperCase()} (${formData.exchange})`,
    });

    setIsSaving(false);

    if (success) {
      handleCancel();
    } else {
      setSaveError('Failed to save symbol');
    }
  };

  const handleDelete = async (base: string) => {
    if (!window.confirm(`Delete symbol ${base}? This cannot be undone.`)) {
      return;
    }

    await removeSymbol(base);
  };

  const handleInputChange = (field: keyof SymbolDefinition, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Column definitions (inside component to capture handlers)
  const symbolColumns: ColumnDef<SymbolDefinition, unknown>[] = useMemo(() => [
    {
      accessorKey: 'base',
      header: 'Base',
      cell: info => <span className="smp-cell-base">{String(info.getValue())}</span>,
    },
    {
      accessorKey: 'fullSymbol',
      header: 'Full Symbol',
      cell: info => <span className="smp-cell-full">{String(info.getValue())}</span>,
    },
    {
      accessorKey: 'tickSize',
      header: 'Tick',
      cell: info => <span className="smp-cell-num">{String(info.getValue())}</span>,
    },
    {
      accessorKey: 'pointValue',
      header: 'Point $',
      cell: info => <span className="smp-cell-num">${(info.getValue() as number).toFixed(2)}</span>,
    },
    {
      accessorKey: 'exchange',
      header: 'Exchange',
      cell: info => <span className="smp-cell-exchange">{String(info.getValue())}</span>,
    },
    {
      accessorKey: 'enabled',
      header: 'Status',
      cell: info => {
        const enabled = info.getValue() as boolean;
        return (
          <span className={`smp-status ${enabled ? 'active' : 'inactive'}`}>
            {enabled ? 'Active' : 'Disabled'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const sym = row.original;
        return (
          <div className="smp-cell-actions">
            <button
              className="smp-action-btn smp-action-edit"
              onClick={() => handleEdit(sym)}
              disabled={isEditing}
              title="Edit"
            >
              Edit
            </button>
            <button
              className="smp-action-btn smp-action-delete"
              onClick={() => handleDelete(sym.base)}
              disabled={isEditing}
              title="Delete"
            >
              Del
            </button>
          </div>
        );
      },
    },
  ], [isEditing]);

  if (isLoading && symbols.length === 0) {
    return (
      <div className={`smp-panel ${className || ''}`}>
        <div className="smp-loading">Loading symbols...</div>
      </div>
    );
  }

  return (
    <div className={`smp-panel ${className || ''}`}>
      {/* Header */}
      <div className="smp-header">
        <div className="smp-title">
          <span className="smp-icon">$</span>
          <span>Symbol Registry</span>
        </div>
        <button className="smp-add-btn" onClick={handleAdd} disabled={isEditing}>
          + Add Symbol
        </button>
      </div>

      {/* Error display */}
      {error && <div className="smp-error">{error}</div>}

      {/* Edit Form */}
      {isEditing && (
        <div className="smp-form">
          <div className="smp-form-header">
            {editingSymbol ? `Edit ${editingSymbol.base}` : 'Add New Symbol'}
          </div>

          <div className="smp-form-grid">
            <div className="smp-form-row">
              <label>Base Symbol *</label>
              <input
                type="text"
                value={formData.base}
                onChange={(e) => handleInputChange('base', e.target.value)}
                placeholder="e.g., GC"
                disabled={!!editingSymbol}
                className="smp-input"
              />
            </div>

            <div className="smp-form-row">
              <label>Full Symbol *</label>
              <input
                type="text"
                value={formData.fullSymbol}
                onChange={(e) => handleInputChange('fullSymbol', e.target.value)}
                placeholder="e.g., GCH6.COMEX"
                className="smp-input"
              />
            </div>

            <div className="smp-form-row">
              <label>Label</label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
                placeholder="e.g., GC (Gold)"
                className="smp-input"
              />
            </div>

            <div className="smp-form-row">
              <label>Exchange</label>
              <select
                value={formData.exchange}
                onChange={(e) => handleInputChange('exchange', e.target.value)}
                className="smp-select"
              >
                {EXCHANGES.map(ex => (
                  <option key={ex} value={ex}>{ex}</option>
                ))}
              </select>
            </div>

            <div className="smp-form-row">
              <label>Tick Size *</label>
              <input
                type="number"
                value={formData.tickSize}
                onChange={(e) => handleInputChange('tickSize', parseFloat(e.target.value) || 0)}
                step="0.001"
                min="0.001"
                className="smp-input"
              />
            </div>

            <div className="smp-form-row">
              <label>Tick Value ($)</label>
              <input
                type="number"
                value={formData.tickValue}
                onChange={(e) => handleInputChange('tickValue', parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0.01"
                className="smp-input"
              />
            </div>

            <div className="smp-form-row">
              <label>Point Value ($) *</label>
              <input
                type="number"
                value={formData.pointValue}
                onChange={(e) => handleInputChange('pointValue', parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0.01"
                className="smp-input"
              />
            </div>

            <div className="smp-form-row smp-form-row-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => handleInputChange('enabled', e.target.checked)}
                />
                Enabled
              </label>
            </div>
          </div>

          {saveError && <div className="smp-error">{saveError}</div>}

          <div className="smp-form-actions">
            <button className="smp-btn smp-btn-cancel" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </button>
            <button className="smp-btn smp-btn-save" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Symbol Table */}
      <div className="smp-table-container">
        <DataTable
          data={symbols}
          columns={symbolColumns}
          className="smp-table"
          rowClassName={(sym) => !sym.enabled ? 'smp-row-disabled' : ''}
          emptyMessage="No symbols configured. Click &quot;Add Symbol&quot; to get started."
          sortable={false}
        />
      </div>

      {/* Info */}
      <div className="smp-info">
        <p>
          Add trading symbols here. Changes take effect immediately - no restart needed.
          Make sure Sierra Chart is writing SCID data for new symbols.
        </p>
      </div>
    </div>
  );
};

export default SymbolManagementPanel;
